import { Component, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../services/dialog.service';

@Component({
  selector: 'app-dialog-container',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (dialog.open()) {
      <div
        class="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm"
        role="presentation"
        (click)="dialog.backdropClick()"
      >
        <div
          class="relative w-full max-w-md rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-800 shadow-2xl shadow-slate-900/20 dark:shadow-black/40 overflow-hidden"
          role="dialog"
          aria-modal="true"
          [attr.aria-labelledby]="'wiz-dialog-title'"
          (click)="$event.stopPropagation()"
        >
          <div class="p-6 sm:p-7">
            <div class="flex gap-4">
              <div
                class="shrink-0 flex h-12 w-12 items-center justify-center rounded-full"
                [ngClass]="
                  dialog.variant() === 'danger'
                    ? 'bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400'
                    : 'bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400'
                "
              >
                @if (dialog.variant() === 'danger') {
                  <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                } @else {
                  <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M12 18a6 6 0 100-12 6 6 0 000 12z"
                    />
                  </svg>
                }
              </div>
              <div class="min-w-0 flex-1 pt-0.5">
                <h2 id="wiz-dialog-title" class="text-lg font-semibold text-slate-900 dark:text-white leading-tight">
                  {{ dialog.title() }}
                </h2>
                <p class="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap break-words">
                  {{ dialog.message() }}
                </p>
                @if (dialog.kind() === 'confirmProjectDelete') {
                  <label class="mt-4 flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      class="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      [checked]="dialog.acknowledged()"
                      (change)="dialog.setAcknowledged($any($event.target).checked)"
                    />
                    <span class="text-sm text-slate-600 dark:text-slate-300">{{ dialog.checkboxLabel() }}</span>
                  </label>
                }
                @if (dialog.kind() === 'prompt') {
                  <div class="mt-4">
                    @if (dialog.inputLabel()) {
                      <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">{{ dialog.inputLabel() }}</label>
                    }
                    <input
                      type="text"
                      [ngModel]="dialog.typedValue()"
                      (ngModelChange)="dialog.setTypedValue($event)"
                      [placeholder]="dialog.inputPlaceholder()"
                      class="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                      autocomplete="off"
                      (keydown.enter)="onPromptEnter($event)"
                    />
                  </div>
                }
                @if (dialog.kind() === 'confirmTyped' || (dialog.kind() === 'confirmProjectDelete' && dialog.requireTypedName())) {
                  <div class="mt-4">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                      {{ dialog.inputLabel() }}
                    </label>
                    <input
                      type="text"
                      [ngModel]="dialog.typedValue()"
                      (ngModelChange)="dialog.setTypedValue($event)"
                      [placeholder]="dialog.inputPlaceholder()"
                      class="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500"
                      autocomplete="off"
                      (keydown.enter)="onTypedEnter($event)"
                    />
                    <p class="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                      Type <span class="font-semibold text-slate-700 dark:text-slate-200">{{ dialog.expectedValue() }}</span> to confirm.
                    </p>
                  </div>
                }
              </div>
            </div>

            <div class="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              @if (dialog.kind() === 'confirm' || dialog.kind() === 'confirmTyped' || dialog.kind() === 'confirmProjectDelete' || dialog.kind() === 'prompt') {
                <button
                  type="button"
                  (click)="dialog.cancelClick()"
                  class="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 transition-colors"
                >
                  {{ dialog.cancelText() }}
                </button>
              }
              <button
                type="button"
                (click)="dialog.confirmClick()"
                [disabled]="(dialog.kind() === 'confirmTyped' && !dialog.typedValueMatches()) || (dialog.kind() === 'confirmProjectDelete' && !dialog.projectDeleteReady()) || (dialog.kind() === 'prompt' && !dialog.promptReady())"
                class="w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                [ngClass]="
                  dialog.variant() === 'danger'
                    ? 'bg-red-600 hover:bg-red-500 shadow-red-600/25 disabled:hover:bg-red-600'
                    : 'bg-blue-600 hover:bg-blue-500 shadow-blue-600/25 disabled:hover:bg-blue-600'
                "
              >
                {{ dialog.confirmText() }}
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class DialogContainerComponent {
  dialog = inject(DialogService);

  onPromptEnter(event: Event): void {
    if (!this.dialog.promptReady()) return;
    event.preventDefault();
    this.dialog.confirmClick();
  }

  onTypedEnter(event: Event): void {
    if (this.dialog.kind() === 'confirmProjectDelete') {
      if (!this.dialog.projectDeleteReady()) return;
    } else if (!this.dialog.typedValueMatches()) {
      return;
    }
    event.preventDefault();
    this.dialog.confirmClick();
  }

  @HostListener('document:keydown', ['$event'])
  onEscape(e: KeyboardEvent): void {
    if (e.key !== 'Escape' || !this.dialog.open()) return;
    e.preventDefault();
    this.dialog.escape();
  }
}
